import { Grid, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { FileDto } from '../../../services/dtos/file.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { ProposalAssignmentDto, SearchProposalAssignmentsDto } from '../../../services/dtos/assignments.dto';
import { ShowMessage, useSnackbars } from '../../../hooks/snackbar.hook';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import CreateIcon from '@mui/icons-material/Create';
import ProposalsAssignmentsForm from '../../forms/assignment/proposals-assignments.form';
import NoData from '../../commun/no-data';
import StyledTable from '../../commun/table';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import OpenFile from '../../action/open-file';
import { serviceSearchProposalsAssignments } from '../../../services/assignments.service';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ProposalAssignmentDialogForm from '../../forms/assignment/proposal-assignment-dialog/proposal-assignment-dialog.form';

/**
 * Ui de l'onglet decision & decomposition
 * @constructor
 */
export default function ProposalsAssignmentsUi(props: {
    mandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
    handlerDownloadFile(file: FileDto): void;
    handlerOpenFile(file: FileDto | undefined): void;
    handleConfirmationProposalAssignment(): void;
    confirmationProposalAssignment: boolean;
}) {
    //SnackBar pour les messages
    const showMessage = useSnackbars();

    //Critères de recherche onglet decisions et deliberations
    const [searchProposalsAssignments, setSearchProposalsAssignments] = useState<SearchProposalAssignmentsDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandate.id,
    });
    //Searching
    const [searching, setSearching] = useState<boolean>(false);
    //Ouvrir la popup de confirmation
    const [open, setOpen] = useState<boolean>(false);
    //Proposal choisie pour la confirmation
    const [proposal, setChosenProposal] = useState<ProposalAssignmentDto>();
    //type de confirmation choisie (refus ou validation) la confirmation
    const [confirmation, setConfirmation] = useState<boolean>();
    // result tableau decisions
    const [proposalAssignments, setProposalsAssignments] = useState<ResultSearchDto<ProposalAssignmentDto>>();

    //Lancement de la recherche a chaque changement
    useEffect(() => {
        let isMounted = true;

        setSearching(true);
        const searchProposalAssignmentsDto: SearchProposalAssignmentsDto = {
            ...searchProposalsAssignments,
        };

        serviceSearchProposalsAssignments(searchProposalAssignmentsDto)
            .then(async result => {
                if (!isMounted) {
                    return;
                }

                if (result) {
                    await manageFile(result, showMessage);
                }
                setProposalsAssignments(result);
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des données');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchProposalsAssignments, props.confirmationProposalAssignment, showMessage]);

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchProposalsAssignments({
            ...searchProposalsAssignments,
            page: newPage,
        });
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchProposalsAssignments({
            ...searchProposalsAssignments,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    //handler sort
    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof ProposalAssignmentDto) => {
        setSearchProposalsAssignments({
            ...searchProposalsAssignments,
            order: order,
            sort: sort,
        });
    };

    //Handler qui gère la fermeture de la popup
    const handleClose = () => {
        setOpen(false);
    };

    //Handler qui gère la fermeture de la popup
    const handlerConfirmation = (chosenProposal: ProposalAssignmentDto, confirmationParam: boolean) => {
        setChosenProposal(chosenProposal);
        setConfirmation(confirmationParam);
        setOpen(true);
    };

    // Mise en place du formulaire à renvoyer côté back
    const handlerSearch = (paramForm: ProposalAssignmentDto) => {
        if (searchProposalsAssignments) {
            const newSearchData: SearchProposalAssignmentsDto = {
                ...searchProposalsAssignments,
                paramReference: paramForm.reference,
                paramcodeTBB: paramForm.codeTBB,
                paramName: paramForm.name,
                paramPlace: paramForm.place,
            };
            setSearchProposalsAssignments(newSearchData);
        }
    };

    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<ProposalAssignmentDto, keyof ProposalAssignmentDto>[] = [
        { id: 'reference', label: 'Référence', sort: false, width: 15 },
        { id: 'codeTBB', label: 'Code TBB', sort: false, width: 15 },
        { id: 'name', label: 'Nom', sort: false, width: 30 },
        { id: 'place', label: 'Lieu', sort: true, width: 20 },
    ];

    //Action à mettre dans le tableau, affichage et vue du document
    const action = (proposalAssignment: ProposalAssignmentDto) => {
        return (
            <>
                {proposalAssignment.electronicSignatureAvailable === true && (
                    <Tooltip title={'Valider par signature électronique'}>
                        <CheckCircleOutlineIcon
                            sx={{
                                color: '#95be72',
                                width: '30px',
                                height: '30px',
                                cursor: 'pointer',
                                marginRight: '5px',
                            }}
                            onClick={() => handlerConfirmation(proposalAssignment, true)}
                            data-testid={'validation-proposal'}
                        />
                    </Tooltip>
                )}
                <Tooltip title={'Refuser'}>
                    <HighlightOffOutlinedIcon
                        sx={{ color: 'red', width: '30px', height: '30px', cursor: 'pointer', marginLeft: '5px' }}
                        onClick={() => handlerConfirmation(proposalAssignment, false)}
                        data-testid={'refusal-proposal'}
                    />
                </Tooltip>
                {proposalAssignment.finalDoc && (
                    <OpenFile
                        handlerOpenFile={props.handlerOpenFile}
                        handlerDownloadFile={props.handlerDownloadFile}
                        file={proposalAssignment.finalDoc}
                    />
                )}
            </>
        );
    };

    //Premet de conditionner l'affichage du tableau des decisions
    const showTable = () => {
        // Données pas encore chargées
        if (proposalAssignments === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        // Données nulles
        if (proposalAssignments?.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        } else {
            return (
                <>
                    <StyledTable
                        data={proposalAssignments?.items}
                        searching={searching}
                        headCells={headCells}
                        action={action}
                        totalItems={proposalAssignments?.totalItems}
                        page={searchProposalsAssignments?.page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeItemsPerPage}
                        itemsPerPage={searchProposalsAssignments?.itemsPerPage}
                        handlerSortBy={handlerSortBy}
                        order={searchProposalsAssignments?.order}
                        sort={searchProposalsAssignments?.sort}
                    ></StyledTable>
                </>
            );
        }
    };

    //Retourne la vue
    return (
        <Grid container className={'tab-content proposal-assignment-ui'} rowSpacing={1}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title'}>
                    <CreateIcon /> Validation électronique des propositions d'affectations
                </Typography>
            </Grid>
            <Grid item xs={12} md={1} />
            <Grid item xs={12} md={10}>
                <Stack>
                    {/*On affiche le formulaire*/}
                    <ProposalsAssignmentsForm handlerSearch={handlerSearch} searching={searching} />
                    {/* On affiche ensuite les réunions terminées */}
                    {showTable()}
                </Stack>
                <ProposalAssignmentDialogForm
                    open={open}
                    mandateId={props.mandate.id}
                    proposalAssignment={proposal}
                    handleConfirmationProposalAssignment={props.handleConfirmationProposalAssignment}
                    confirmation={confirmation}
                    handleClose={handleClose}
                />
            </Grid>
        </Grid>
    );
}

/**
 * Méthode qui permet de récupérer le pdf correctement
 * @param result document
 * @param showMessage showMessage
 */
async function manageFile(result: ResultSearchDto<ProposalAssignmentDto>, showMessage: ShowMessage) {
    for (const doc of result.items) {
        // Conversion du contenu en pdf si un doc est renvoyer format string
        if (doc.document?.file && doc.document?.fileName) {
            const docFile = doc.document?.file;
            const fileName = doc.document?.fileName;
            await fetch(doc.document?.file)
                .then(async pdf => {
                    // On transforme le contenu en blob
                    const content = await pdf.blob();
                    doc.finalDoc = { name: fileName, data: docFile, content: content };
                })
                .catch(error => {
                    console.error('Erreur lors de la conversion du document.');
                    showAxiosMessageError(error, showMessage);
                });
        }
    }
}
